import { useEffect, useState } from "react";
import { Link, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, fmtUSD } from "../lib/api";
import { Package, Heart, User as UserIcon, ShoppingBag, Trash2, Shield, Printer, ArrowRight } from "lucide-react";

export default function AccountPage() {
    const { user, checking } = useAuth();
    if (checking) return <div className="pt-52 text-center text-secondary">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    return (
        <div data-testid="account-page" className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="mb-10">
                    <div className="data-label text-brand mb-3">RESEARCHER DASHBOARD</div>
                    <h1 className="font-serif text-5xl">Welcome, {user.name?.split(" ")[0] || "Researcher"}.</h1>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <aside className="lg:col-span-3">
                        <AccountNav />
                    </aside>
                    <main className="lg:col-span-9">
                        <Routes>
                            <Route index element={<AccountHome />} />
                            <Route path="orders" element={<OrdersList />} />
                            <Route path="wishlist" element={<WishlistList />} />
                            <Route path="profile" element={<Profile />} />
                        </Routes>
                    </main>
                </div>
            </div>
        </div>
    );
}

function AccountNav() {
    const loc = useLocation();
    const items = [
        { to: "/account", label: "Overview", icon: UserIcon },
        { to: "/account/orders", label: "Orders", icon: ShoppingBag },
        { to: "/account/wishlist", label: "Wishlist", icon: Heart },
        { to: "/account/profile", label: "Profile", icon: Package },
    ];
    return (
        <nav className="space-y-1 lg:sticky lg:top-32">
            {items.map((it) => {
                const Icon = it.icon;
                const active = loc.pathname === it.to;
                return (
                    <Link key={it.to} to={it.to} className={`flex items-center gap-3 px-4 py-3 border ${active ? "border-[var(--brand)] text-brand bg-[var(--brand-soft)]" : "border-transparent text-secondary hover:text-primary"}`}>
                        <Icon size={14} />
                        <span className="font-mono text-xs uppercase tracking-wider">{it.label}</span>
                    </Link>
                );
            })}
        </nav>
    );
}

function AccountHome() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [orders, setOrders] = useState([]);
    useEffect(() => { api.get("/orders/mine").then((r) => setOrders(r.data)); }, []);
    return (
        <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Stat label="Orders Placed" value={orders.length} />
                <Stat label="First-Order Discount" value={user.first_order_discount_used ? "USED" : "10% AVAILABLE"} brand={!user.first_order_discount_used} />
                <Stat label="Account Status" value="ACTIVE" />
            </div>

            {isAdmin && (
                <div data-testid="admin-tools-panel">
                    <div className="data-label text-brand mb-3">ADMIN TOOLS</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Link
                            to="/admin"
                            data-testid="dashboard-admin-tile"
                            className="card p-5 flex items-center justify-between hover:border-[var(--brand)] transition-colors group"
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 flex items-center justify-center bg-[var(--brand-soft)] border border-[var(--brand)]">
                                    <Shield size={18} className="text-brand" />
                                </div>
                                <div>
                                    <div className="font-serif text-lg text-primary">Admin Dashboard</div>
                                    <div className="data-label text-[0.6rem]">Orders · Customers · Subscribers · Coupons</div>
                                </div>
                            </div>
                            <ArrowRight size={16} className="text-brand opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Link>
                        <Link
                            to="/label-designer"
                            data-testid="dashboard-label-tile"
                            className="card p-5 flex items-center justify-between hover:border-[var(--brand)] transition-colors group"
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 flex items-center justify-center bg-[var(--brand-soft)] border border-[var(--brand)]">
                                    <Printer size={18} className="text-brand" />
                                </div>
                                <div>
                                    <div className="font-serif text-lg text-primary">Label Designer</div>
                                    <div className="data-label text-[0.6rem]">Generate print-ready SVG label packs</div>
                                </div>
                            </div>
                            <ArrowRight size={16} className="text-brand opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Link>
                    </div>
                </div>
            )}

            <div>
                <h2 className="font-serif text-2xl mb-5">Recent Orders</h2>
                {orders.length === 0 ? (
                    <div className="card p-8 text-center text-secondary">No orders yet. <Link to="/shop" className="link-gold">Browse the catalog</Link>.</div>
                ) : (
                    <div className="space-y-3">
                        {orders.slice(0, 4).map((o) => <OrderRow key={o.id} order={o} />)}
                    </div>
                )}
            </div>
        </div>
    );
}

function OrdersList() {
    const [orders, setOrders] = useState([]);
    useEffect(() => { api.get("/orders/mine").then((r) => setOrders(r.data)); }, []);
    return (
        <div>
            <h2 className="font-serif text-3xl mb-6">All Orders</h2>
            {orders.length === 0 ? (
                <div className="card p-8 text-center text-secondary">No orders yet.</div>
            ) : (
                <div className="space-y-3">
                    {orders.map((o) => <OrderRow key={o.id} order={o} />)}
                </div>
            )}
        </div>
    );
}

function OrderRow({ order }) {
    return (
        <div className="card p-5" data-testid={`order-row-${order.order_number}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="data-label text-brand">{order.order_number}</div>
                    <div className="font-mono text-xs text-secondary mt-1">{new Date(order.created_at).toLocaleDateString()} · {order.items.length} items · {order.payment_method.toUpperCase()}</div>
                </div>
                <div className="text-right">
                    <div className="pill pill-gold">{order.status.replace("_", " ").toUpperCase()}</div>
                    <div className="font-mono text-brand mt-2">{fmtUSD(order.total)}</div>
                </div>
            </div>
        </div>
    );
}

function WishlistList() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/wishlist").then((r) => setItems(r.data)); }, []);
    const remove = async (id) => {
        await api.delete(`/wishlist/${id}`);
        setItems(items.filter((i) => i.id !== id));
    };
    return (
        <div>
            <h2 className="font-serif text-3xl mb-6">Wishlist</h2>
            {items.length === 0 ? (
                <div className="card p-8 text-center text-secondary">No items yet.</div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {items.map((p) => (
                        <div key={p.id} className="card p-4 flex gap-4">
                            <img src={p.image} alt="" className="w-20 h-20 object-cover bg-[var(--bg)]" />
                            <div className="flex-1">
                                <Link to={`/product/${p.slug}`} className="font-serif text-lg hover:text-brand">{p.name}</Link>
                                <div className="data-label mt-1">{p.strength}</div>
                                <div className="font-mono text-brand text-sm mt-2">{fmtUSD(p.price)}</div>
                            </div>
                            <button onClick={() => remove(p.id)} className="btn btn-ghost"><Trash2 size={14} /></button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Profile() {
    const { user } = useAuth();
    return (
        <div>
            <h2 className="font-serif text-3xl mb-6">Profile</h2>
            <div className="card p-6 space-y-3 font-mono text-sm">
                <Row label="NAME" value={user.name} />
                <Row label="EMAIL" value={user.email} />
                <Row label="ROLE" value={user.role.toUpperCase()} />
                <Row label="MEMBER SINCE" value={new Date(user.created_at || Date.now()).toLocaleDateString()} />
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between border-b border-[var(--border)] pb-3 last:border-0">
            <span className="text-muted text-xs">{label}</span>
            <span className="text-primary">{value}</span>
        </div>
    );
}

function Stat({ label, value, brand }) {
    return (
        <div className="card p-5">
            <div className="data-label">{label}</div>
            <div className={`font-serif text-3xl mt-2 ${brand ? "text-brand" : ""}`}>{value}</div>
        </div>
    );
}
